import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  Grid,
  Pagination,
  Typography,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useSelector, useDispatch } from 'react-redux';
import {
  getSupplierCatalog,
  getSupplierCategories,
  getSupplierInfo,
} from '../../api/suppliers';
import Loading from '../../components/Loading';
import { PublicCatalogLayout } from './PublicCatalogLayout';
import styles from './supplierCatalog.module.scss';
import notImage from '../../assets/product-default.png';
import { addToCart, updateCartPrices } from '../../redux/slices/catalogCartSlice';
import { CartDrawer } from '../../components/CartDrawer';
import { FloatingCartButton } from '../../components/CartButton';
import { CartButton } from '../../components/CartButton';

const categoryEmojis = {
  'Frutas y Verduras': '🥬',
  Carnicos: '🥩',
  Abarrote: '🍞',
  Abarrotes: '🍞',
  Bebidas: '🥤',
  'Material operativo': '🧂',
  'Pescados y Mariscos': '🐟',
};

export const SupplierCatalog = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useSelector((state) => state?.auth);
  const token = auth?.token || null;
  const restaurant = auth?.restaurant || null;
  const restaurantList = auth?.restaurantList || [];
  const cartItems = useSelector((state) => state?.catalogCart?.items || []);
  const dispatch = useDispatch();

  const currentRestaurant = restaurantList.find((val) => val.id === parseInt(restaurant));
  const restaurantId = currentRestaurant?.id || null;

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [supplierInfo, setSupplierInfo] = useState(null);
  const [total, setTotal] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);

  const ItemsPerPage = 12;

  const handleAddToCart = (product) => {
    dispatch(addToCart(product));
  };

  useEffect(() => {
    loadInitialData();
  }, [id]);

  useEffect(() => {}, [products]);

  useEffect(() => {
    const updateCartItemsPrices = async () => {
      if (restaurantId && token && cartItems.length > 0) {
        try {
          const productIds = cartItems.map((item) => item.id);
          const params = {
            limit: 100,
            page: 1,
            categoryId: '',
            restaurantId: restaurantId,
          };

          const data = await getSupplierCatalog(id, params, token);

          let allProducts = [];
          if (data && data.products && Array.isArray(data.products)) {
            allProducts = data.products;
          } else if (Array.isArray(data)) {
            allProducts = data;
          }

          const cartProductsUpdated = allProducts.filter((product) =>
            productIds.includes(product.id),
          );

          if (cartProductsUpdated.length > 0) {
            dispatch(updateCartPrices(cartProductsUpdated));
          }
        } catch (error) {
          console.error('Error updating cart prices:', error);
        }
      }
    };

    updateCartItemsPrices();
  }, [restaurantId, token]);

  useEffect(() => {
    if (selectedCategoryId !== null) {
      loadProducts();
    }
  }, [currentPage, selectedCategoryId, restaurantId]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [supplierData, categoriesData] = await Promise.all([
        getSupplierInfo(id, token),
        getSupplierCategories(id, token),
      ]);

      if (supplierData) {
        setSupplierInfo(supplierData);
      }

      if (categoriesData) {
        setCategories(categoriesData);
      }

      await loadProducts();
    } catch (error) {
      setProducts([]);
    }
    setLoading(false);
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const params = {
        limit: ItemsPerPage,
        page: currentPage,
        categoryId: selectedCategoryId,
        restaurantId: restaurantId,
      };

      const data = await getSupplierCatalog(id, params, token);

      let productsArray = [];
      let totalCount = 0;

      if (data && data.products && Array.isArray(data.products)) {
        productsArray = data.products;
        totalCount = data.total || 0;
        if (totalCount > 0) {
          setTotal(totalCount);
          setTotalPages(Math.ceil(totalCount / ItemsPerPage));
          setHasNextPage(currentPage * ItemsPerPage < totalCount);
        } else {
          const hasMore = productsArray.length === ItemsPerPage;
          setHasNextPage(hasMore);
        }
      } else if (Array.isArray(data)) {
        productsArray = data;
        const hasMore = productsArray.length === ItemsPerPage;
        setHasNextPage(hasMore);
      }

      setProducts(productsArray);

      if (totalCount === 0) {
        setTotal(currentPage * ItemsPerPage - ItemsPerPage + productsArray.length);
      }
    } catch (error) {
      setProducts([]);
      setTotal(0);
      setTotalPages(1);
      setHasNextPage(false);
    }
    setLoading(false);
  };

  const handlePageChange = (_event, page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCategorySelect = (categoryName, categoryId = '') => {
    setSelectedCategory(categoryName);
    setSelectedCategoryId(categoryId);
    setCurrentPage(1);
  };

  if (loading && products.length === 0) {
    return (
      <PublicCatalogLayout supplierName={supplierInfo?.name}>
        <Box
          sx={{
            minHeight: '80vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Loading />
        </Box>
      </PublicCatalogLayout>
    );
  }

  const isAuthenticated = auth?.status === 'authenticated';

  return (
    <PublicCatalogLayout supplierName={supplierInfo?.name}>
      {isAuthenticated && <FloatingCartButton />}
      <CartDrawer />
      <Box sx={{ paddingY: 4, width: '100%', boxSizing: 'border-box' }}>
        {/* Supplier Info Header */}
        {supplierInfo && (
          <Box
            sx={{
              paddingX: { xs: 2, md: 10 },
              marginBottom: 4,
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 3,
                paddingY: 3,
                paddingX: { xs: 1, md: 2 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flex: 1 }}>
                {supplierInfo.logo ? (
                  <Box
                    component="img"
                    src={supplierInfo.logo}
                    alt={supplierInfo.name}
                    sx={{
                      width: { xs: 80, md: 100 },
                      height: { xs: 80, md: 100 },
                      objectFit: 'contain',
                      borderRadius: '12px',
                      border: '1px solid #e0e0e0',
                      padding: 1,
                      backgroundColor: '#fff',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      fontSize: { xs: '52px', md: '74px' },
                      lineHeight: 1,
                    }}
                  >
                    🛒
                  </Box>
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    sx={{
                      color: '#333',
                      marginBottom: 0.5,
                      fontSize: { xs: '1.5rem', md: '2.125rem' },
                    }}
                  >
                    {supplierInfo.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#49a35b',
                      fontWeight: 500,
                      marginBottom: 0.5,
                      fontSize: '15px',
                    }}
                  >
                    Catálogo de productos
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', fontSize: '14px' }}>
                    Explora nuestros productos por categorías
                  </Typography>
                </Box>
              </Box>
              {isAuthenticated && (
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                  <CartButton color="#49a35b" />
                </Box>
              )}
            </Box>

            {/* Separator Line */}
            <Box
              sx={{
                width: { xs: '85%', md: '75%' },
                height: '1px',
                backgroundColor: '#e0e0e0',
                marginTop: 3.0,
                marginX: 'auto',
              }}
            />
          </Box>
        )}

        {/* Categories Filter */}
        <Box
          sx={{
            paddingX: { xs: 0, md: 6 },
            marginBottom: 4,
            display: 'flex',
            justifyContent: { xs: 'flex-start', md: 'center' },
            flexWrap: { xs: 'nowrap', md: 'wrap' },
            gap: 1.5,
            overflowX: { xs: 'auto', md: 'visible' },
            overflowY: 'hidden',
            paddingY: { xs: 1, md: 0 },
            '&::-webkit-scrollbar': {
              height: '6px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#49a35b',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: '#3d8a4d',
            },
          }}
        >
          <Chip
            label="🌟 Todos"
            onClick={() => handleCategorySelect('Todos', '')}
            sx={{
              fontSize: { xs: 14, md: 16 },
              padding: { xs: '8px 14px', md: '10px 18px' },
              height: 'auto',
              color: selectedCategory === 'Todos' ? '#fff' : '#000',
              backgroundColor: selectedCategory === 'Todos' ? '#49a35b' : '#e8f5e8',
              border: selectedCategory === 'Todos' ? 'none' : '2px solid #49a35b',
              fontWeight: selectedCategory === 'Todos' ? 'bold' : 'normal',
              flexShrink: 0,
              marginLeft: { xs: 2, md: 0 },
              '&:hover': {
                backgroundColor: selectedCategory === 'Todos' ? '#3d8a4d' : '#d4edd4',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 8px rgba(73, 163, 91, 0.3)',
              },
              transition: 'all 0.2s',
            }}
          />
          {categories &&
            categories.map((category, index) => {
              const emoji = categoryEmojis[category.name] || '📦';
              const isLast = index === categories.length - 1;
              return (
                <Chip
                  key={index}
                  label={`${emoji} ${category.name}`}
                  onClick={() => handleCategorySelect(category.name, category.id)}
                  sx={{
                    fontSize: { xs: 14, md: 16 },
                    padding: { xs: '8px 14px', md: '10px 18px' },
                    height: 'auto',
                    color: selectedCategory === category.name ? '#fff' : '#000',
                    backgroundColor:
                      selectedCategory === category.name ? '#49a35b' : '#e8f5e8',
                    border:
                      selectedCategory === category.name ? 'none' : '2px solid #49a35b',
                    fontWeight: selectedCategory === category.name ? 'bold' : 'normal',
                    flexShrink: 0,
                    marginRight: { xs: isLast ? 2 : 0, md: 0 },
                    '&:hover': {
                      backgroundColor:
                        selectedCategory === category.name ? '#3d8a4d' : '#d4edd4',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(73, 163, 91, 0.3)',
                    },
                    transition: 'all 0.2s',
                  }}
                />
              );
            })}
        </Box>

        {/* Products Grid */}
        <Box
          sx={{
            paddingX: { xs: 2, md: 6 },
            minHeight: '60vh',
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 10 }}>
              <Loading />
            </Box>
          ) : products && products.length > 0 ? (
            <>
              <Box
                sx={{
                  marginBottom: 3,
                }}
              >
                <Typography variant="body1" sx={{ color: '#666', fontWeight: 500 }}>
                  📦 Mostrando {products.length} de {total} productos
                </Typography>
              </Box>
              <Grid
                marginTop={2}
                paddingBottom={3}
                sx={{ paddingX: { xs: 0, md: 0, lg: 3 }, width: '100%' }}
                container
                spacing={3}
              >
                {products.map((product) => {
                  const emoji =
                    product.categoryName +
                    ' ' +
                    (categoryEmojis[product.categoryName] || '📦');
                  return (
                    <Grid key={product.id} item md={6} lg={3} xs={12}>
                      <Card
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: '16px',
                          overflow: 'visible',
                          position: 'relative',
                          transition: 'transform 0.2s, box-shadow 0.2s',
                          cursor: 'pointer',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: '0 12px 24px rgba(73, 163, 91, 0.2)',
                          },
                        }}
                        onClick={() =>
                          navigate(`/proveedor/${id}/catalogo/${product.id}`)
                        }
                      >
                        {/* Category Tag */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 12,
                            left: 12,
                            backgroundColor: '#49a35b',
                            color: '#fff',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            zIndex: 1,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                          }}
                        >
                          {emoji} {product.category}
                        </Box>

                        <CardMedia
                          component="img"
                          height="200"
                          image={product.image || notImage}
                          alt={product.name}
                          sx={{ objectFit: 'cover' }}
                        />

                        <CardContent
                          sx={{
                            flexGrow: 1,
                            padding: 2,
                            paddingBottom: '8px !important',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            sx={{
                              marginBottom: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              minHeight: '2.8em',
                              fontSize: '15px',
                              color: '#333',
                            }}
                          >
                            {product.name}
                          </Typography>

                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              marginTop: 'auto',
                              marginBottom: 1,
                            }}
                          >
                            <Box>
                              <Typography
                                variant="h6"
                                sx={{
                                  color: '#49a35b',
                                  fontWeight: 'bold',
                                  fontSize: '20px',
                                }}
                              >
                                ${product.price}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#999',
                                  fontSize: '11px',
                                }}
                              >
                                por {product.measurementUnit || 'unidad'}
                              </Typography>
                            </Box>

                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddToCart(product);
                              }}
                              sx={{
                                backgroundColor: '#49a35b',
                                color: '#fff',
                                width: 40,
                                height: 40,
                                '&:hover': {
                                  backgroundColor: '#3d8a4d',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s',
                              }}
                            >
                              <AddIcon />
                            </IconButton>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          ) : (
            <Box
              sx={{
                textAlign: 'center',
                padding: 10,
                backgroundColor: '#fff',
                borderRadius: 4,
              }}
            >
              <Typography variant="h6" color="text.secondary">
                😔 No se encontraron productos
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ marginTop: 1 }}>
                Intenta con otra categoría
              </Typography>
            </Box>
          )}
        </Box>

        {/* Pagination */}
        {products.length > 0 && (currentPage > 1 || hasNextPage || totalPages > 1) && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              paddingY: 6,
            }}
          >
            {totalPages > 1 ? (
              <Pagination
                page={currentPage}
                count={totalPages}
                onChange={handlePageChange}
                size="large"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    color: '#49a35b',
                    fontWeight: 'bold',
                    '&:hover': {
                      backgroundColor: '#e8f5e8',
                    },
                  },
                  '& .Mui-selected': {
                    backgroundColor: '#49a35b !important',
                    color: '#fff',
                    '&:hover': {
                      backgroundColor: '#3d8a4d !important',
                    },
                  },
                }}
              />
            ) : (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  onClick={() => handlePageChange(null, currentPage - 1)}
                  disabled={currentPage === 1}
                  variant="contained"
                  sx={{
                    backgroundColor: currentPage === 1 ? '#e0e0e0' : '#49a35b',
                    color: '#fff',
                    fontWeight: 'bold',
                    paddingX: 3,
                    '&:hover': {
                      backgroundColor: currentPage === 1 ? '#e0e0e0' : '#3d8a4d',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#e0e0e0',
                      color: '#999',
                    },
                  }}
                >
                  ←
                </Button>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    paddingX: 3,
                    paddingY: 1,
                    backgroundColor: '#e8f5e8',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    color: '#49a35b',
                    fontSize: '16px',
                  }}
                >
                  {currentPage}
                </Box>
                <Button
                  onClick={() => handlePageChange(null, currentPage + 1)}
                  disabled={!hasNextPage}
                  variant="contained"
                  sx={{
                    backgroundColor: !hasNextPage ? '#e0e0e0' : '#49a35b',
                    color: '#fff',
                    fontWeight: 'bold',
                    paddingX: 3,
                    '&:hover': {
                      backgroundColor: !hasNextPage ? '#e0e0e0' : '#3d8a4d',
                    },
                    '&.Mui-disabled': {
                      backgroundColor: '#e0e0e0',
                      color: '#999',
                    },
                  }}
                >
                  →
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </PublicCatalogLayout>
  );
};
